// AdminAlumniBatchWiseDialog.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
  Button,
  Paper,
  Typography,
  TextField,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { API_ENDPOINTS } from "config/api";
import { decryptText } from "config/encryption";
import safeToast from "utils/safeToast";
import axiosInstance from "API/axiosInstance";


// Get staffId and token from localStorage
const getAdminSession = () => {
  try {
    const session = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
    if (session) {
      const decrypted = decryptText(session);
      if (decrypted) {
        const parsed = JSON.parse(decrypted);
        return {
          staffId: parsed.StaffId || parsed.staffId || parsed.StaffCode || parsed.staffCode || null,
          token: parsed.Token || parsed.token || null
        };
      }
    }
  } catch (error) {
    console.error("Error extracting admin session:", error);
  }
  return { staffId: null, token: null };
};

const { staffId, token } = getAdminSession();
const AdminAlumniBatchWiseDialog = ({ open, onClose }) => {
  const gridRef = useRef(null);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filter states
  const [registrationType, setRegistrationType] = useState("1");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");

  // Filter options
  const registrationOptions = [
    { value: "0", label: "All Alumni" },
    { value: "1", label: "Registered Users" },
    { value: "2", label: "Unregistered Alumni" }
  ];

  // Generate batch options dynamically from current year to 1900
  const generateBatchOptions = () => {
    const currentYear = new Date().getFullYear();
    const options = [
      { value: "", label: "-" } // All batches option
    ]; 
    for (let year = currentYear; year >= 1900; year--) {
      options.push({ value: year.toString(), label: year.toString() });
    }
    return options;
  };

  const batchOptions = generateBatchOptions();

  // Column definitions for AG Grid
  const columnDefs = useMemo(() => [
    {
      headerName: "S.No",
      valueGetter: (params) => {
        const api = params.api;
        const rowIdx = params.node ? params.node.rowIndex : 0;
        const hasPagination = !!api.paginationGetPageSize;
        if (hasPagination) {
          const page = api.paginationGetCurrentPage?.() ?? 0;
          const pageSize = api.paginationGetPageSize?.() ?? 0;
          return pageSize ? page * pageSize + rowIdx + 1 : rowIdx + 1;
        }
        return rowIdx + 1;
      },
      width: 60,
      minWidth: 50,
      maxWidth: 70,
      resizable: false,
      sortable: false,
      filter: false,
      pinned: "left",
      suppressSizeToFit: true,
      suppressMenu: true,
      cellStyle: { textAlign: "center", fontSize: '0.7rem' },
    },
    { field: "fullName", headerName: "Full Name", sortable: true, filter: true, flex: 2, minWidth: 150 },
    { field: "rollnum", headerName: "Roll Number", sortable: true, filter: true, flex: 1, minWidth: 120 },
    { field: "customGender", headerName: "Gender", sortable: true, filter: true, flex: 1, minWidth: 100 },
    { 
      field: "customDob", 
      headerName: "Date of Birth", 
      sortable: true, 
      filter: true, 
      flex: 1, 
      minWidth: 120,
      valueGetter: (params) => params.data?.customDob || params.data?.CustomDob || ""
    },
    { field: "customBatch", headerName: "Batch", sortable: true, filter: true, flex: 1, minWidth: 80 },
    { field: "customCollegeStudied", headerName: "College", sortable: true, filter: true, flex: 2, minWidth: 150 },
    { field: "customCourseStudied", headerName: "Course", sortable: true, filter: true, flex: 1.5, minWidth: 120 },
    { field: "customBranch", headerName: "Branch", sortable: true, filter: true, flex: 1, minWidth: 100 },
    { field: "customWhatsappNumber", headerName: "WhatsApp", sortable: true, filter: true, flex: 1.5, minWidth: 120 },
    { field: "customPresentStatus", headerName: "Status", sortable: true, filter: true, flex: 1.5, minWidth: 150 },
    { field: "customOrganization", headerName: "Organization", sortable: true, filter: true, flex: 2, minWidth: 180 },
    { field: "customDesignation", headerName: "Designation", sortable: true, filter: true, flex: 1.5, minWidth: 140 },
    { field: "customLocation", headerName: "Location", sortable: true, filter: true, flex: 1, minWidth: 120 },
    { field: "customAvailability", headerName: "Availability", sortable: true, filter: true, flex: 1, minWidth: 120 },
  ], []);

  // Grid defaults
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
    minWidth: 100,
    cellStyle: { fontSize: '0.7rem' },
  }), []);

  // Fetch batch-wise alumni data
  const fetchBatchWiseData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(API_ENDPOINTS.ADMIN.GET_ALUMNUS_FULL_DETAILS_BATCHWISE, {
        registedType: registrationType,
        batch: selectedBatch,
        course: selectedCourse
      });
      setRowData(response.data);
    } catch (error) {
      console.error("Error fetching batch-wise alumni data:", error);
      safeToast.error("Failed to fetch batch-wise alumni data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [registrationType, selectedBatch, selectedCourse]);

  // Grid ready handler
  const onGridReady = useCallback((params) => {
    gridRef.current = params.api;
    params.api.sizeColumnsToFit({ defaultMinWidth: 100 });
  }, []);

  // Export CSV function
  const exportCsv = useCallback(() => {
    if (!rowData || rowData.length === 0) {
      safeToast.warn('No data to export');
      return;
    }

    try {
      const headers = ['S.No', 'Full Name', 'Roll Number', 'Gender', 'Batch', 'College', 'Course', 'Branch', 'WhatsApp', 'Status', 'Organization', 'Designation', 'Location', 'Availability'];
      const csvContent = [
        headers.join(','),
        ...rowData.map((row, index) => [
          index + 1,
          `"${(row.fullName || '').replace(/"/g, '""')}"`,
          `"${(row.rollnum || '').replace(/"/g, '""')}"`,
          `"${(row.customGender || '').replace(/"/g, '""')}"`,
          `"${(row.customBatch || '').replace(/"/g, '""')}"`,
          `"${(row.customCollegeStudied || '').replace(/"/g, '""')}"`,
          `"${(row.customCourseStudied || '').replace(/"/g, '""')}"`,
          `"${(row.customBranch || '').replace(/"/g, '""')}"`,
          `"${(row.customWhatsappNumber || '').replace(/"/g, '""')}"`,
          `"${(row.customPresentStatus || '').replace(/"/g, '""')}"`,
          `"${(row.customOrganization || '').replace(/"/g, '""')}"`,
          `"${(row.customDesignation || '').replace(/"/g, '""')}"`,
          `"${(row.customLocation || '').replace(/"/g, '""')}"`,
          `"${(row.customAvailability || '').replace(/"/g, '""')}"`,
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `alumni-batch-wise-${selectedBatch}-${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSV export error:', error);
      safeToast.error('Failed to export CSV. Please try again.');
    }
  }, [rowData, selectedBatch]);

  // Load data when dialog opens or filters change
  useEffect(() => {
    if (open) {
      fetchBatchWiseData();
    }
  }, [open, fetchBatchWiseData]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      {/* Title + Close Button */}
      <DialogTitle sx={{ pr: 8, fontWeight: "bold", color: "primary.main" }}>
        Alumni Details - Batch Wise
        <Box sx={{ position: "absolute", top: 8, right: 8 }}>
          <Tooltip title="Close">
            <IconButton onClick={onClose} color="error">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Filter Controls */}
            <Paper variant="outlined" sx={{ m: 2, p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                  Filter Options
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {rowData.length} records found
                </Typography>
              </Box>
              
              <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 180 }}
                  options={registrationOptions}
                  value={registrationOptions.find(opt => opt.value === registrationType) || registrationOptions[0]}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setRegistrationType(newValue.value);
                    }
                  }}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Registration Type"
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '0.8rem' },
                        '& .MuiInputBase-input': { fontSize: '0.8rem' }
                      }}
                    />
                  )}
                />

                <Autocomplete
                  size="small"
                  sx={{ minWidth: 120 }}
                  options={batchOptions}
                  value={batchOptions.find(opt => opt.value === selectedBatch) || batchOptions[0]}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelectedBatch(newValue.value);
                    }
                  }}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Batch"
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '0.8rem' },
                        '& .MuiInputBase-input': { fontSize: '0.8rem' }
                      }}
                    />
                  )}
                />              

                <Button                  
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={fetchBatchWiseData}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Refresh
                </Button>

                <Button                 
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={exportCsv}
                  disabled={rowData.length === 0}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Export CSV
                </Button>
              </Box>
            </Paper>

            {/* AG Grid */}
            <div className="ag-theme-alpine" style={{ height: "calc(100vh - 200px)", width: "99%", margin: "0 8px" }}>
              <AgGridReact
                ref={gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                onGridReady={onGridReady}
                animateRows={true}
                suppressRowClickSelection={true}
                rowHeight={28}
                headerHeight={35}
                pagination={true}
                paginationPageSize={15}
                suppressMenuHide={true}
              />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

AdminAlumniBatchWiseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminAlumniBatchWiseDialog;
